import type {
    CreditBalance,
    GiftCertificateApplyResponse,
    GiftCertificateEmailResponse,
    GiftCertificatePurchaseResponse,
    InvoicesResponse,
} from '~/types/person/financial';
import { pbRequest, pbRequestRaw } from '~/services/request';

interface GiftCertificatePurchasePayload {
    name: string;
    email: string;
    amount: number;
    payment_method?: string;
}

// Pure API service for billing operations - no state management
export const billingService = {
    async getInvoices(personId: number, page: number = 1, perPage: number = 10): Promise<InvoicesResponse> {
        const nuxtApp = useNuxtApp();

        // The page itself is the answer: the paginator sits beside `data`, not inside it.
        return await pbRequestRaw<InvoicesResponse>(
            nuxtApp.$api.routes.people.billing.invoices.get(personId, page, perPage),
            { failure: 'Failed to fetch invoices' }
        );
    },

    async downloadInvoice(personId: number, invoiceId: string): Promise<Blob> {
        const nuxtApp = useNuxtApp();

        return await pbRequestRaw<Blob>(
            nuxtApp.$api.routes.people.billing.invoices.download(personId, invoiceId),
            { responseType: 'blob', failure: 'Failed to download invoice' }
        );
    },

    async getCreditBalance(personId: number): Promise<number> {
        const nuxtApp = useNuxtApp();

        const balance = await pbRequest<CreditBalance>(nuxtApp.$api.routes.people.billing.creditBalance(personId), {
            failure: 'Failed to fetch credit balance',
        });

        return balance.credit_balance || 0;
    },

    async purchaseGiftCertificate(
        personId: number,
        name: string,
        email: string,
        amount: number,
        paymentMethodId?: string
    ): Promise<GiftCertificatePurchaseResponse> {
        const nuxtApp = useNuxtApp();

        const payload: GiftCertificatePurchasePayload = { name, email, amount };

        // Add payment method if provided
        if (paymentMethodId) {
            payload.payment_method = paymentMethodId;
        }

        return await pbRequestRaw<GiftCertificatePurchaseResponse>(
            nuxtApp.$api.routes.people.billing.giftCertificates.purchase(personId),
            {
                method: 'POST',
                body: payload,
                expect: 201,
                failure: 'Failed to purchase gift certificate',
            }
        );
    },

    async applyGiftCertificate(personId: number, code: string): Promise<GiftCertificateApplyResponse> {
        const nuxtApp = useNuxtApp();

        return await pbRequestRaw<GiftCertificateApplyResponse>(
            nuxtApp.$api.routes.people.billing.giftCertificates.apply(personId),
            {
                method: 'POST',
                body: { code },
                expect: 201,
                failure: 'Failed to apply gift certificate',
            }
        );
    },

    async sendGiftCertificateEmail(
        personId: number,
        recipientName: string,
        recipientEmail: string,
        code: string
    ): Promise<GiftCertificateEmailResponse> {
        const nuxtApp = useNuxtApp();

        return await pbRequestRaw<GiftCertificateEmailResponse>(
            nuxtApp.$api.routes.people.billing.giftCertificates.email(personId, code),
            {
                method: 'POST',
                body: { recipientName, recipientEmail },
                failure: 'Failed to send gift certificate email',
            }
        );
    },
};
